/* eslint-disable css-modules/no-unused-class */
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Design System
import unknownStoreIcon from 'jl-design-system/assets/images/maps/unknown-store-map-pin.svg';
import env from 'jl-design-system/utils/env/env';

// Config
import urlConstants from '@constants/urlConstants';
import {
  onGoogleMapsScriptLoading,
  onGoogleMapsScriptLoadSuccess,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import { loadScript } from '@utils/index';
import { deepEqual } from '@utils/object';

// Components
import Heading from '../heading';

// Styles
import styles from './google-map.scss';

export class GoogleMap extends Component {
  static propTypes = {
    activeMarker: PropTypes.shape({
      id: PropTypes.string,
      location: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
      }),
    }),
    className: PropTypes.string,
    googleMapsApiKey: PropTypes.string,
    googleMapsScriptLoaded: PropTypes.bool,
    googleMapsScriptLoading: PropTypes.bool,
    handleMarkerClick: PropTypes.func,
    isStoreSelectable: PropTypes.bool,
    mapName: PropTypes.string,
    markers: PropTypes.arrayOf(PropTypes.object),
    onGoogleMapsScriptLoadSuccess: PropTypes.func,
    onGoogleMapsScriptLoading: PropTypes.func,
    searchTerm: PropTypes.string,
  };

  static defaultProps = {
    activeMarker: {},
    className: 'mapContainer',
    googleMapsApiKey: '',
    googleMapsScriptLoaded: false,
    googleMapsScriptLoading: false,
    handleMarkerClick: window.defaultFunc,
    isStoreSelectable: false,
    mapName: '',
    markers: [],
    onGoogleMapsScriptLoadSuccess: window.defaultFunc,
    onGoogleMapsScriptLoading: window.defaultFunc,
    searchTerm: '',
  };

  static getSmallIconSize() {
    // eslint-disable-next-line no-undef
    return new google.maps.Size(52, 64);
  }

  static getLargeIconSize() {
    // eslint-disable-next-line no-undef
    return new google.maps.Size(72, 89);
  }

  static getIconImage(stores, store) {
    if (store.mapPin) {
      return store.mapPin;
    }

    return unknownStoreIcon;
  }

  static getIconSize(markers, activeMarkerId, currentMarkerId) {
    if (markers.length > 1 && activeMarkerId !== currentMarkerId) {
      return GoogleMap.getSmallIconSize();
    }

    return GoogleMap.getLargeIconSize();
  }

  static getAnchorPoint(collectionPoint, iconSize) {
    const x = iconSize.width / 2;
    // eslint-disable-next-line no-undef
    return new google.maps.Point(x, iconSize.height);
  }

  constructor(props) {
    super(props);

    this.state = {
      showMapLoadingError: false,
    };

    this.googleMaps = undefined;
  }

  componentDidMount() {
    const {
      googleMapsScriptLoaded,
      googleMapsScriptLoading,
      onGoogleMapsScriptLoading,
      onGoogleMapsScriptLoadSuccess,
      googleMapsApiKey,
    } = this.props;

    setTimeout(() => {
      // prevent issue where refresh the map view causes map not to load
      if (googleMapsScriptLoaded) {
        this.initialiseMap();
        this.updateBounds();
      } else if (!googleMapsScriptLoading) {
        onGoogleMapsScriptLoading();

        const googleMapsUrl = urlConstants.GOOGLEMAPS_API_URL;
        const url = `${googleMapsUrl}${googleMapsApiKey}&loading=async&callback=initGoogleMap&libraries=marker`;
        const mapContainerId = 'mapContainer';

        window.initGoogleMap = this.initialiseMap;
        if (document.getElementById(mapContainerId)) {
          loadScript({
            url,
            appendToNodeId: mapContainerId,
            onSuccessCallback: onGoogleMapsScriptLoadSuccess,
            onErrorCallback: this.onScriptLoadError,
          })();
          this.updateBounds();
        }
      }
    }, 0);
  }

  componentDidUpdate(prevProps) {
    const { markers, searchTerm } = this.props;

    if (!deepEqual(prevProps.markers, markers)) {
      this.updateMarkers();
      this.updateBounds();

      if (deepEqual(prevProps.searchTerm, searchTerm)) {
        this.map.setCenter(this.centerPointMarker ? this.centerPointMarker.getPosition() : '0, 0');
      } else {
        this.createCenterPointMarker();
      }
    } else if (markers.length > 1 && this.markers) {
      const oldActiveMarkerIndex = markers.findIndex(m => (m.id === prevProps.activeMarker.id));
      this.updateActiveMarker(oldActiveMarkerIndex);
    }
  }

  onScriptLoadError = () => {
    this.setState({
      showMapLoadingError: true,
    });
  };

  updateMarker = ({ active = false, index }) => {
    const { markers } = this.props;
    const marker = this.markers[index];
    const iconImage = GoogleMap.getIconImage(markers, markers[index]);
    let iconSize = GoogleMap.getSmallIconSize();
    let zIndex = 2;
    if (active) {
      iconSize = GoogleMap.getLargeIconSize();
      zIndex = 999;
    }

    const imgElement = marker.content;
    imgElement.src = iconImage;
    imgElement.style.width = `${iconSize.width}px`;
    imgElement.style.height = `${iconSize.height}px`;
    marker.zIndex = zIndex;
  };

  updateActiveMarker = (oldActiveMarkerIndex) => {
    const { activeMarker, markers } = this.props;
    const index = markers.findIndex(m => (m.id === activeMarker.id));

    if (oldActiveMarkerIndex !== -1) {
      this.updateMarker({
        index: oldActiveMarkerIndex,
      });
    }

    this.updateMarker({
      active: true,
      index,
    });
  };

  initialiseMap = () => {
    this.googleMaps =window?.google?.maps?.Map;

    const {
      mapName,
      markers,
      activeMarker,
      isStoreSelectable,
      searchTerm,
    } = this.props;
    const mapId = env.isClientNonProd ? 'af6acd145c77fe5b' : '874d682c53047f93';

    if (!this.map && document.getElementById(`map-${mapName}`)) {
      const centerCoordinates = isStoreSelectable ? {
        lat: activeMarker.location.latitude,
        lng: activeMarker.location.longitude,
      } : {
        lat: markers[0].location.latitude,
        lng: markers[0].location.longitude,
      };
      // eslint-disable-next-line
      this.map = new google.maps.Map(document.getElementById(`map-${mapName}`), {
        zoom: 15,
        center: centerCoordinates,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        mapId,
      });

      if (searchTerm && isStoreSelectable) {
        this.createCenterPointMarker();
      }
    }

    this.updateMarkers();
    this.updateBounds();
  };

  createMarker = (collectionPoint, iconImage, iconSize) => {
    const { location } = collectionPoint;

    const iconElement = document.createElement('img');
    iconElement.src = iconImage;
    iconElement.style.width = `${iconSize.width}px`;
    iconElement.style.height = `${iconSize.height}px`;

    // eslint-disable-next-line no-undef
    return new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: {
        lat: location.latitude,
        lng: location.longitude,
      },
      content: iconElement,
      zIndex: 1,
    });
  };

  createCenterPointMarker = () => {
    if (this.googleMaps) {
      if (this.centerPointMarker) {
        this.centerPointMarker.setMap(null);
      }

      const { isStoreSelectable, searchTerm } = this.props;
      // eslint-disable-next-line
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({
        address: searchTerm,
        componentRestrictions: {
          country: 'UK',
        },
      }, (result) => {
        if (result && result[0]) {
          const { activeMarker } = this.props;

          const invisibleElement = document.createElement('div');
          invisibleElement.style.width = '0';
          invisibleElement.style.height = '0';
          invisibleElement.style.opacity = '0';

          // eslint-disable-next-line
          this.centerPointMarker = new google.maps.marker.AdvancedMarkerElement({
            content: invisibleElement,
            map: this.map,
            position: {
              lat: result[0].geometry.location.lat(),
              lng: result[0].geometry.location.lng(),
            },
          });

          const centerCoordinates = isStoreSelectable ? {
            lat: activeMarker.location.latitude,
            lng: activeMarker.location.longitude,
          } : {
            lat: result[0].geometry.location.lat(),
            lng: result[0].geometry.location.lng(),
          };

          this.map.setCenter(centerCoordinates);
          this.updateBounds();
        }
      });

    }
  };

  removeAllMarkers = () => {
    this.markers.forEach(marker => marker.setMap(null));
  };

  updateBounds = () => {
    if (this.googleMaps) {
      if (this.markers && this.markers.length > 1) {
        // eslint-disable-next-line no-undef
        const bounds = new google.maps.LatLngBounds();

        this.markers.forEach((marker, index) => {
          if (index <= 3) {
            const position = marker.position;
            // eslint-disable-next-line no-undef
            bounds.extend(new google.maps.LatLng(position.lat, position.lng));
          }
        });

        this.map.fitBounds(bounds);
      }
    }
  };

  updateMarkers = () => {
    if (this.googleMaps) {
      const {
        activeMarker,
        handleMarkerClick,
        markers,
      } = this.props;

      if (this.markers) {
        this.removeAllMarkers();
      }

      this.markers = markers.map((m) => {
        const {
          id,
        } = m;
        const iconImage = GoogleMap.getIconImage(markers, m);
        const iconSize = GoogleMap.getIconSize(markers, activeMarker.id, id);
        const marker = this.createMarker(m, iconImage, iconSize);
        const zindex = activeMarker.id === id ? 999 : 1;
        marker.zIndex = zindex;

        if (handleMarkerClick) {
          // eslint-disable-next-line
          google.maps.event.addListener(marker, 'click', () => {
            handleMarkerClick(m);
          });
        }

        return marker;
      });
    }
  };

  render() {
    const {
      className,
      mapName,
    } = this.props;

    const {
      showMapLoadingError,
    } = this.state;

    const mapId = `map-${mapName}`;

    return (
      <div
        aria-hidden="true"
        className={className}
        data-testid="map-container"
        id="mapContainer"
      >

        <div className={styles.map} data-testid="map" id={mapId}>
          {
            showMapLoadingError &&
            <div className={styles.mapLoadErrorMessage} data-testid="google-map-error-message">
              <Heading
                label="Something went wrong"
                marginBottom="2"
                tag="h2"
                type="s"
              />
              <p>Sorry, due to a technical problem we have been unable to show a map.</p>
            </div>
          }
        </div>

      </div>
    );
  }
}

export function mapStateToProps(state) {
  return {
    googleMapsApiKey: state.config?.frontendKeys?.googleMapsApiKey,
    googleMapsScriptLoaded: state.clickAndCollect.googleMapsScriptLoaded,
    googleMapsScriptLoading: state.clickAndCollect.googleMapsScriptLoading,
  };
}

export function mapDispatchToProps() {
  return {
    onGoogleMapsScriptLoading,
    onGoogleMapsScriptLoadSuccess,
  };
}

export default connect(state => mapStateToProps(state), mapDispatchToProps())(GoogleMap);
